/**
 * Dựng Three.js mesh từ part WVM. Hình học **căn tâm tại gốc local** —
 * vị trí trong scene do pivot (MotionEngine / SceneManager) đặt.
 * Port thuật toán bo góc + cutout từ bản thử nghiệm, viết lại module hóa + typed.
 */
import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  Color,
  DoubleSide,
  EdgesGeometry,
  ExtrudeGeometry,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Matrix3,
  Matrix4,
  Mesh,
  MeshPhongMaterial,
  Shape,
  Vector2,
  Vector3,
} from 'three';
import { mergeGeometries, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Brush, Evaluator, SUBTRACTION } from 'three-bvh-csg';
import { DEFAULT_COLORS, SCALE } from './constants';
import {
  isBox,
  isCylinder,
  isExtrusion,
  isMeshInline,
  isMeshRef,
  type ExtrusionGeometry,
  type MeshAsset,
  type MeshInlineGeometry,
  type MeshRefGeometry,
  type WvmDocument,
  type WvmPart,
} from '../lib/types/wvm';

const S = SCALE;

/** Ngưỡng góc (độ) lọc cạnh mesh: chỉ hiện nếp gấp/đường bao, ẩn cạnh tam giác trên mặt phẳng/cong mịn. */
const MESH_EDGE_THRESHOLD = 40;

/** Bảng tra màu materialId → hex. */
export function buildColorMap(doc: WvmDocument): Map<string, string> {
  const map = new Map<string, string>();
  for (const m of doc.materials ?? []) map.set(m.id, m.color);
  return map;
}

export function resolveColor(part: WvmPart, colorMap: Map<string, string>): string {
  if (part.materialId && colorMap.has(part.materialId)) {
    return colorMap.get(part.materialId)!;
  }
  return DEFAULT_COLORS[part.category ?? 'fallback'] ?? DEFAULT_COLORS.fallback;
}

// ---------- hình học bo góc (ExtrudeGeometry) ----------

function makeExtrudedRounded(fW: number, fH: number, depth: number, r: number): BufferGeometry {
  if (r <= 0.001) return new BoxGeometry(fW, fH, depth);
  let hw = fW / 2 - r;
  let hh = fH / 2 - r;
  if (hw < 0) hw = 0;
  if (hh < 0) hh = 0;
  const shape = new Shape();
  shape.moveTo(-hw, -fH / 2);
  shape.lineTo(hw, -fH / 2);
  shape.absarc(hw, -hh, r, -Math.PI / 2, 0, false);
  shape.lineTo(fW / 2, hh);
  shape.absarc(hw, hh, r, 0, Math.PI / 2, false);
  shape.lineTo(-hw, fH / 2);
  shape.absarc(-hw, hh, r, Math.PI / 2, Math.PI, false);
  shape.lineTo(-fW / 2, -hh);
  shape.absarc(-hw, -hh, r, Math.PI, (Math.PI * 3) / 2, false);
  const geo = new ExtrudeGeometry(shape, { steps: 1, depth, bevelEnabled: false });
  geo.translate(0, 0, -depth / 2);
  geo.computeVertexNormals();
  return geo;
}

function buildRoundedGeo(wx: number, hy: number, dz: number, radius: number): BufferGeometry {
  const W = wx * S;
  const H = hy * S;
  const D = dz * S;
  const minD = Math.min(W, H, D);
  const r = Math.min(radius * S, minD * 0.45);
  if (r < 0.01) return new BoxGeometry(W, H, D);
  // bo theo mặt mỏng nhất
  if (D <= minD) return makeExtrudedRounded(W, H, D, r);
  if (W <= minD) {
    const geo = makeExtrudedRounded(D, H, W, r);
    geo.rotateY(Math.PI / 2);
    return geo;
  }
  const geo = makeExtrudedRounded(W, D, H, r);
  geo.rotateX(-Math.PI / 2);
  return geo;
}

// ---------- panel uốn (extrusion) ----------

/** Tâm world (mm) của khối extrusion = tâm AABB của shape×height qua transform. */
function extrusionWorldCenterMM(geom: ExtrusionGeometry): Vector3 {
  const m = new Matrix4().fromArray(geom.transform);
  const min = new Vector3(Infinity, Infinity, Infinity);
  const max = new Vector3(-Infinity, -Infinity, -Infinity);
  const v = new Vector3();
  for (const [x, y] of geom.shape) {
    for (const z of [0, geom.height]) {
      v.set(x, y, z).applyMatrix4(m);
      min.min(v);
      max.max(v);
    }
  }
  return min.add(max).multiplyScalar(0.5);
}

/**
 * UV cho khối đùn — theo mm THẬT (như box/mesh-inline): VÁCH bên (mặt chính của panel UỐN) map
 * (arc-length dọc chu vi shape, z = chiều đùn/chiều cao); NẮP (mặt chính của panel PHẲNG-cong) map
 * (x, y) của shape. → grain tile đều cả 2 chiều, KHÔNG kéo sọc.
 *
 * Bản cũ (`planarUVGenerator`) dùng (x,y) cho CẢ vách → UV KHÔNG đổi dọc z → với panel uốn (mặt chính
 * là vách, cao theo chiều đùn) texture bị KÉO SỌC DỌC chiều cao. UV tính ở KHÔNG GIAN SHAPE (mm; trước
 * applyMatrix4/scale nên không đổi). SceneManager coi extrusion là `uvSpaceMm` (repeat = 1/repeatMm).
 */
function extrusionMmUVGenerator(pts: ExtrusionGeometry['shape']) {
  // arc-length tích luỹ dọc CHU VI shape (mm) → U cho vách; khoá theo (x,y) lượng tử hoá (~0.02mm).
  const arc = new Map<string, number>();
  const key = (x: number, y: number): string => `${Math.round(x * 50)},${Math.round(y * 50)}`;
  let acc = 0;
  arc.set(key(pts[0][0], pts[0][1]), 0);
  for (let i = 1; i < pts.length; i++) {
    acc += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    arc.set(key(pts[i][0], pts[i][1]), acc);
  }
  const uWall = (i: number, v: number[]): number => arc.get(key(v[i * 3], v[i * 3 + 1])) ?? v[i * 3];
  const cap = (i: number, v: number[]): Vector2 => new Vector2(v[i * 3], v[i * 3 + 1]); // (x,y) mm
  const wall = (i: number, v: number[]): Vector2 => new Vector2(uWall(i, v), v[i * 3 + 2]); // (arc,z) mm
  return {
    generateTopUV(_g: ExtrudeGeometry, v: number[], a: number, c: number, d: number): Vector2[] {
      return [cap(a, v), cap(c, v), cap(d, v)];
    },
    generateSideWallUV(
      _g: ExtrudeGeometry,
      v: number[],
      a: number,
      c: number,
      d: number,
      e: number,
    ): Vector2[] {
      return [wall(a, v), wall(c, v), wall(d, v), wall(e, v)];
    },
  };
}

/** Dựng khối đùn từ mặt cắt 2D kín; CĂN TÂM tại gốc (pivot đặt lại vị trí), đã scale. */
function buildExtrusionGeo(geom: ExtrusionGeometry): BufferGeometry {
  const pts = geom.shape;
  const shape = new Shape();
  shape.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][1]);
  shape.closePath();
  const geo = new ExtrudeGeometry(shape, {
    depth: geom.height,
    bevelEnabled: false,
    steps: 1,
    UVGenerator: extrusionMmUVGenerator(pts), // UV mm: vách (arc,z), nắp (x,y) — SceneManager uvSpaceMm
  });
  geo.applyMatrix4(new Matrix4().fromArray(geom.transform)); // → world (mm)
  const c = extrusionWorldCenterMM(geom);
  geo.translate(-c.x, -c.y, -c.z); // căn tâm tại gốc local
  geo.scale(S, S, S); // mm → scene units
  geo.computeVertexNormals();
  return geo;
}

// ---------- mesh nhúng (triangle soup world-space mm) ----------

/** Tâm world (mm) = tâm AABB của triangle soup. */
function meshWorldCenterMM(geom: MeshInlineGeometry): { x: number; y: number; z: number } {
  const p = geom.positions;
  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity,
    maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;
  for (let i = 0; i < p.length; i += 3) {
    minX = Math.min(minX, p[i]);
    maxX = Math.max(maxX, p[i]);
    minY = Math.min(minY, p[i + 1]);
    maxY = Math.max(maxY, p[i + 1]);
    minZ = Math.min(minZ, p[i + 2]);
    maxZ = Math.max(maxZ, p[i + 2]);
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2, z: (minZ + maxZ) / 2 };
}

/** Dựng mesh nhúng; CĂN TÂM tại gốc (pivot đặt lại vị trí), đã scale (như extrusion). */
function buildMeshGeo(geom: MeshInlineGeometry): BufferGeometry {
  const c = meshWorldCenterMM(geom);
  const src = geom.positions;
  const arr = new Float32Array(src.length);
  for (let i = 0; i < src.length; i += 3) {
    arr[i] = (src[i] - c.x) * S; // căn tâm tại gốc + scale
    arr[i + 1] = (src[i + 1] - c.y) * S;
    arr[i + 2] = (src[i + 2] - c.z) * S;
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(arr, 3));
  if (geom.normals && geom.normals.length === src.length) {
    geo.setAttribute('normal', new Float32BufferAttribute(new Float32Array(geom.normals), 3));
  } else {
    geo.computeVertexNormals(); // soup → pháp tuyến phẳng theo mặt (faceted) — ổn cho đan mây
  }
  // UV per-vertex (mm) nếu plugin xuất → lát texture theo mm như box (04f §2.2b).
  if (geom.uvs && geom.uvs.length === (src.length / 3) * 2) {
    geo.setAttribute('uv', new Float32BufferAttribute(new Float32Array(geom.uvs), 2));
  }
  return geo;
}

/**
 * Nguồn cạnh cho mesh-inline: hàn đỉnh trùng THEO VỊ TRÍ (bỏ normal/uv để các tam giác kề
 * dùng chung đỉnh) → index hoá. Nhờ đó `EdgesGeometry` phát hiện cạnh chung và lọc theo ngưỡng
 * góc — chỉ còn nếp gấp + đường bao, KHÔNG vẽ mọi cạnh tam giác (tránh rối như soup chưa index).
 */
function meshEdgeSource(geo: BufferGeometry): BufferGeometry {
  const pos = new BufferGeometry();
  pos.setAttribute('position', geo.getAttribute('position'));
  return mergeVertices(pos, 1e-3);
}

// ---------- mesh-ref / instancing dùng-chung geometry (Đòn C, WVM 1.5) ----------

/**
 * Đòn C: dựng geometry LOCAL của 1 asset — DÙNG CHUNG cho mọi instance (không scale, không căn tâm;
 * pivot mỗi part mang `Scale(S)∘transform` để đặt vào scene). Nhờ dùng chung 1 BufferGeometry,
 * vertex buffer chỉ nạp GPU 1 lần / asset (đúng mục tiêu giảm parse-heap + VRAM). Toạ độ mm.
 */
export function buildAssetGeometry(asset: MeshAsset): BufferGeometry {
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(new Float32Array(asset.positions), 3));
  if (asset.normals && asset.normals.length === asset.positions.length) {
    geo.setAttribute('normal', new Float32BufferAttribute(new Float32Array(asset.normals), 3));
  } else {
    geo.computeVertexNormals();
  }
  if (asset.uvs && asset.uvs.length === (asset.positions.length / 3) * 2) {
    geo.setAttribute('uv', new Float32BufferAttribute(new Float32Array(asset.uvs), 2));
  }
  return geo;
}

/** Cạnh dùng-chung cho asset: EdgesGeometry tính 1 lần, mọi instance tham chiếu chung (không dựng lại 56×). */
export function buildAssetEdges(assetGeo: BufferGeometry): EdgesGeometry {
  return new EdgesGeometry(meshEdgeSource(assetGeo), MESH_EDGE_THRESHOLD);
}

/**
 * Ma trận đặt instance mesh-ref vào scene: `Scale(S) ∘ transform` (transform: LOCAL mm → world mm;
 * Scale(S): world mm → scene units). Rigid (không scale méo/mirror ở v1) → normals xoay đúng bằng 3×3.
 */
export function meshRefPivotMatrix(geom: MeshRefGeometry): Matrix4 {
  const m = new Matrix4().fromArray(geom.transform);
  return m.premultiply(new Matrix4().makeScale(S, S, S));
}

/**
 * Dựng Mesh cho 1 instance mesh-ref TĨNH: dùng chung `assetGeo` + `assetEdges` (đã tính sẵn), material
 * RIÊNG (giữ màu/chọn per-part). Geometry ở gốc asset-local; SceneManager đặt pivot bằng meshRefPivotMatrix.
 */
export function buildMeshRefMesh(
  part: WvmPart,
  hexColor: string,
  assetGeo: BufferGeometry,
  assetEdges: EdgesGeometry,
): Mesh {
  const mesh = new Mesh(assetGeo, makePhong(hexColor, false));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.partId = part.id;
  attachEdges(mesh, assetEdges);
  return mesh;
}

/**
 * Instance mesh-ref CÓ MOTION: KHÔNG gộp (cần pivot/căn-tâm riêng cho animate). Bung về mesh-inline
 * world-space (áp transform vào đỉnh + xoay normals) rồi đi đúng đường mesh-inline sẵn có — parity đầy đủ.
 */
export function expandMeshRefToInline(geom: MeshRefGeometry, asset: MeshAsset): MeshInlineGeometry {
  const m = new Matrix4().fromArray(geom.transform);
  const p = asset.positions;
  const out = new Array<number>(p.length);
  const v = new Vector3();
  for (let i = 0; i < p.length; i += 3) {
    v.set(p[i], p[i + 1], p[i + 2]).applyMatrix4(m);
    out[i] = v.x;
    out[i + 1] = v.y;
    out[i + 2] = v.z;
  }
  const res: MeshInlineGeometry = { kind: 'mesh', positions: out };
  if (asset.normals && asset.normals.length === p.length) {
    const nm = new Matrix3().getNormalMatrix(m);
    const nOut = new Array<number>(p.length);
    for (let i = 0; i < p.length; i += 3) {
      v.set(asset.normals[i], asset.normals[i + 1], asset.normals[i + 2]).applyMatrix3(nm).normalize();
      nOut[i] = v.x;
      nOut[i + 1] = v.y;
      nOut[i + 2] = v.z;
    }
    res.normals = nOut;
  }
  if (asset.uvs && asset.uvs.length === (p.length / 3) * 2) res.uvs = asset.uvs.slice();
  return res;
}

// ---------- material ----------

function makePhong(hex: string, metallic: boolean): MeshPhongMaterial {
  return new MeshPhongMaterial({
    color: new Color(hex),
    specular: new Color(metallic ? 0x555555 : 0x222222),
    shininess: metallic ? 80 : 30,
    transparent: true,
    opacity: 1,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1,
  });
}

function addEdges(mesh: Mesh, edgeSource: BufferGeometry, thresholdDeg = 1): void {
  attachEdges(mesh, new EdgesGeometry(edgeSource, thresholdDeg));
}

/** Gắn 2 LineSegments (nét thấy + nét khuất) dùng CHUNG `edges` — tách khỏi addEdges để instance mesh-ref chia sẻ EdgesGeometry. */
function attachEdges(mesh: Mesh, edges: EdgesGeometry): void {
  // nét NHÌN THẤY: depthTest mặc định → bị tấm phía trước che (hidden-line). Vẽ sau, đậm.
  const visible = new LineSegments(
    edges,
    new LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.2 }),
  );
  visible.userData.isEdge = true;
  mesh.add(visible);
  // nét KHUẤT: CÙNG geometry, KHÔNG depthTest → luôn vẽ kể cả khi bị che; mờ. Mặc định ẩn
  // (opacity 0), chỉ bật ở wireframe/x-ray (applyAppearance) để tương phản với nét nhìn thấy.
  const hidden = new LineSegments(
    edges,
    new LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0, depthTest: false }),
  );
  hidden.userData.isEdgeHidden = true;
  mesh.add(hidden);
}

function addCutouts(mesh: Mesh, part: WvmPart): void {
  if (!isBox(part.geometry) || !part.geometry.cutouts?.length) return;
  const g = part.geometry;
  for (const cut of g.cutouts!) {
    const cGeo = new BoxGeometry(cut.wx * S, cut.hy * S, cut.dz * S);
    const cMesh = new Mesh(cGeo, new MeshPhongMaterial({ color: 0x0d0d0d }));
    cMesh.position.set((cut.cx - g.cx) * S, (cut.cy - g.cy) * S, (cut.cz - g.cz) * S);
    cMesh.renderOrder = 1;
    cMesh.add(
      new LineSegments(new EdgesGeometry(cGeo), new LineBasicMaterial({ color: 0x333333 })),
    );
    mesh.add(cMesh);
  }
}

/** Tuỳ chọn dựng mesh. `recessGrooves` (04d): khoét rãnh LÕM THẬT vào box phẳng (CSG) thay vì decal. */
export interface BuildPartOpts {
  recessGrooves?: boolean;
}

/**
 * Dựng mesh cho 1 part. Geometry căn tâm tại gốc; userData.partId gắn để raycast.
 * Hỗ trợ box/cylinder/extrusion/mesh-inline. Trả null cho mesh-uri (SketchUp .glb) — chưa hỗ trợ.
 * 04d: `opts.recessGrooves` + box phẳng + có `grooves` → khoét lõm thật (CSG); thành công thì
 * `mesh.userData.carvedGrooves=true` (caller KHÔNG thêm decal). Thất bại → giữ box, caller vẽ decal.
 */
export function buildPartMesh(part: WvmPart, hexColor: string, opts: BuildPartOpts = {}): Mesh | null {
  const geom = part.geometry;
  let geometry: BufferGeometry;
  let edgeSource: BufferGeometry;
  let metallic = false;
  let edgeThreshold = 1;
  let carved = false;
  let carveable = false; // 04d: geometry là solid phẳng/cong khoét được (box không bo / extrusion / mesh-inline)

  if (isCylinder(geom)) {
    const r = geom.radius * S;
    const h = geom.height * S;
    geometry = new CylinderGeometry(r, r, h, 24);
    if (geom.axis === 'x') geometry.rotateZ(Math.PI / 2);
    else if (geom.axis === 'z') geometry.rotateX(Math.PI / 2);
    edgeSource = geometry;
    metallic = true;
  } else if (isBox(geom)) {
    const hasRound = (geom.round ?? 0) > 0;
    geometry = hasRound
      ? buildRoundedGeo(geom.wx, geom.hy, geom.dz, geom.round!)
      : new BoxGeometry(geom.wx * S, geom.hy * S, geom.dz * S);
    // bo góc → dùng box phẳng làm nguồn cạnh cho gọn (như bản cũ)
    edgeSource = hasRound ? new BoxGeometry(geom.wx * S, geom.hy * S, geom.dz * S) : geometry;
    carveable = !hasRound; // box bo góc → không khoét (giữ decal)
  } else if (isExtrusion(geom)) {
    geometry = buildExtrusionGeo(geom);
    edgeSource = geometry;
    // mặt cong = nhiều facet nhỏ; threshold cao để chỉ hiện cạnh thật (góc/đầu mút)
    edgeThreshold = 30;
    carveable = true;
  } else if (isMeshInline(geom)) {
    geometry = buildMeshGeo(geom);
    // hàn đỉnh + lọc ngưỡng góc → chỉ nếp gấp/đường bao (không vẽ mọi tam giác)
    edgeSource = meshEdgeSource(geometry);
    edgeThreshold = MESH_EDGE_THRESHOLD;
    carveable = true;
  } else {
    return null; // mesh-uri (SketchUp .glb) — chưa hỗ trợ
  }

  // 04d: khoét rãnh LÕM THẬT (CSG) cho MỌI solid khoét được — box phẳng / extrusion(cong) / mesh-inline.
  // Thất bại (null) → giữ geometry gốc, caller vẽ decal. Model thật: tấm rãnh toàn extrusion/mesh (0 box).
  if (opts.recessGrooves && carveable && part.grooves?.length) {
    const cv = carveGrooves(geometry, part.grooves, partCenter(part));
    if (cv) {
      // Nét LẤY TỪ OUTLINE PANEL TRƯỚC KHOÉT (giữ `edgeSource`/threshold theo kind), KHÔNG từ lưới CSG:
      // CSG chia lại mặt phẳng → cạnh lẻ T-junction (mặt lớn chưa cắt kề nhiều tam giác nhỏ) mà
      // EdgesGeometry luôn vẽ → ra "đường chéo" rối. Recess vẫn hiện nhờ độ sâu + đổ bóng.
      if (edgeSource === geometry) edgeSource = geometry.clone(); // tách khỏi geometry sắp dispose
      geometry.dispose();
      geometry = cv;
      carved = true;
    }
  }

  const mesh = new Mesh(geometry, makePhong(hexColor, metallic));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.partId = part.id;
  if (carved) mesh.userData.carvedGrooves = true; // caller: KHÔNG thêm decal

  if (!metallic) addEdges(mesh, edgeSource, edgeThreshold);
  addCutouts(mesh, part);

  return mesh;
}

// ---------- nẹp / edge band (04a) ----------

/** 04a: màu nẹp mặc định khi user CHƯA gán (02a — nguồn số DUY NHẤT; không chép nơi khác). */
export const DEFAULT_BAND_COLOR = '#CFCFCF';

/**
 * 04a: geometry ruy-băng nẹp trong hệ CON của `part.mesh` (mesh mang bù chuyển động — motion-correct).
 * `positions`/`normals` = triangle soup mm model-space; `center` = `partCenter(part)` (ĐÃ scale) →
 * local = pos*S − center (khớp cách part mesh căn tâm). `normals` là HƯỚNG → không scale/offset;
 * thiếu/không khớp độ dài → tự tính flat (soup không index).
 */
export function buildBandGeo(
  positions: number[],
  normals: number[] | undefined,
  center: [number, number, number],
): BufferGeometry {
  const [px, py, pz] = center;
  const arr = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    arr[i] = positions[i] * S - px;
    arr[i + 1] = positions[i + 1] * S - py;
    arr[i + 2] = positions[i + 2] * S - pz;
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(arr, 3));
  if (normals && normals.length === positions.length) {
    geo.setAttribute('normal', new Float32BufferAttribute(new Float32Array(normals), 3));
  } else {
    geo.computeVertexNormals();
  }
  return geo;
}

/**
 * 04a: material nẹp — `DoubleSide` (winding ribbon từ extractor không chắc) + `polygonOffset`
 * (chống z-fight với mặt panel; positions đã gồm phần lồi `thickness` nên KHÔNG đẩy theo pháp tuyến).
 */
export function makeBandMaterial(hexColor: string): MeshPhongMaterial {
  const m = new MeshPhongMaterial({ color: new Color(hexColor), side: DoubleSide, shininess: 20 });
  m.polygonOffset = true;
  m.polygonOffsetFactor = -1;
  m.polygonOffsetUnits = -1;
  return m;
}

// ---------- rãnh xoi / groove (04c) ----------

/** 04c: màu rãnh khắc — tái dùng tông tối cutout (addCutouts) cho nhất quán. */
export const GROOVE_COLOR = 0x0d0d0d;

/** 04c: nhô dải khắc khỏi mặt tấm (mm) — tránh bị mặt panel nuốt ở góc nghiêng (kèm polygonOffset). */
const GROOVE_PROUD_MM = 0.2;

/**
 * 04c: geometry "đường khắc" (decal PHẲNG) cho 1 rãnh — triangle soup trong hệ CON của `part.mesh`
 * (giống `buildBandGeo` → motion-correct). `path` world mm → `*S − center` (center = `partCenter`, đã
 * scale); `normal` = hướng world đơn vị = chiều ĂN VÀO vật liệu (03a §2). Mỗi đoạn `path` → 1 quad
 * rộng `width` ⊥ đường chạy trên mặt, nhô `GROOVE_PROUD_MM` theo −normal (RA phía người xem, khỏi mặt).
 * Góc polyline nối per-segment (khe nhỏ ở khúc cua = giới hạn v1).
 * `depth` KHÔNG dùng (v1 phẳng). Trả null nếu path < 2 điểm / normal suy biến / không dựng được đoạn nào.
 */
export function buildGrooveGeo(
  path: [number, number, number][],
  width: number,
  normal: [number, number, number],
  center: [number, number, number],
): BufferGeometry | null {
  if (!path || path.length < 2) return null;
  const n = new Vector3(normal[0], normal[1], normal[2]);
  if (n.lengthSq() < 1e-9) return null;
  n.normalize();
  const [px, py, pz] = center;
  const half = (width * S) / 2;
  const proud = GROOVE_PROUD_MM * S;
  // path → scene-local (×S − center) rồi nhô RA khỏi mặt theo −normal (normal ăn vào vật liệu)
  const pts = path.map((p) =>
    new Vector3(p[0] * S - px, p[1] * S - py, p[2] * S - pz).addScaledVector(n, -proud),
  );
  const pos: number[] = [];
  const nrm: number[] = [];
  const dir = new Vector3();
  const wdir = new Vector3();
  for (let i = 0; i < pts.length - 1; i++) {
    dir.subVectors(pts[i + 1], pts[i]);
    if (dir.lengthSq() < 1e-12) continue; // đoạn trùng điểm → bỏ
    wdir.crossVectors(n, dir);
    if (wdir.lengthSq() < 1e-12) continue; // path song song normal (bất thường) → bỏ đoạn
    wdir.normalize().multiplyScalar(half);
    const a = new Vector3().addVectors(pts[i], wdir);
    const b = new Vector3().subVectors(pts[i], wdir);
    const c = new Vector3().addVectors(pts[i + 1], wdir);
    const d = new Vector3().subVectors(pts[i + 1], wdir);
    // 2 tam giác (a,b,c)+(c,b,d); material DoubleSide nên winding không quan trọng
    pos.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    pos.push(c.x, c.y, c.z, b.x, b.y, b.z, d.x, d.y, d.z);
    for (let k = 0; k < 6; k++) nrm.push(n.x, n.y, n.z);
  }
  if (!pos.length) return null;
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(new Float32Array(pos), 3));
  geo.setAttribute('normal', new Float32BufferAttribute(new Float32Array(nrm), 3));
  return geo;
}

/** 04c: material rãnh — tối, `DoubleSide`, `polygonOffset` âm (vẽ trước mặt tấm, chống z-fight như nẹp). */
export function makeGrooveMaterial(): MeshPhongMaterial {
  const m = new MeshPhongMaterial({ color: GROOVE_COLOR, side: DoubleSide, shininess: 10 });
  m.polygonOffset = true;
  m.polygonOffsetFactor = -2;
  m.polygonOffsetUnits = -2;
  return m;
}

/** 04d: nhô viền miệng rãnh RA khỏi mặt (mm, theo −normal) để nét sắc, không z-fight mặt/khe. */
const GROOVE_RIM_LIFT_MM = 0.15;

/**
 * 04d (A+): geometry VIỀN KHUNG rãnh — `LineSegments` (cặp đỉnh) ôm trọn máng rãnh đã khoét:
 *   • MIỆNG (trên mặt mở): 2 cạnh dài men `path` (±width/2 theo `widthDir=cross(normal,pathDir)`) + 2 nắp.
 *   • ĐÁY (lùi `depth` theo +normal = ăn vào vật liệu): 2 cạnh dài + 2 nắp.
 *   • 2 CẠNH ĐỨNG mỗi đầu (nối miệng↔đáy) ở đoạn hợp lệ đầu & cuối.
 * Toạ độ `*S − center` như `buildGrooveGeo`; miệng nhô `GROOVE_RIM_LIFT_MM` theo −normal cho sắc, đáy
 * lùi cùng lượng lift để nổi khỏi mặt đáy. Dùng cho part ĐÃ khoét. Trả null nếu path<2 / normal suy biến.
 */
export function buildGrooveRimGeo(
  path: [number, number, number][],
  width: number,
  depth: number,
  normal: [number, number, number],
  center: [number, number, number],
): BufferGeometry | null {
  if (!path || path.length < 2) return null;
  const n = new Vector3(normal[0], normal[1], normal[2]);
  if (n.lengthSq() < 1e-9) return null;
  n.normalize();
  const [px, py, pz] = center;
  const half = (width * S) / 2;
  const lift = GROOVE_RIM_LIFT_MM * S;
  const depthScene = depth * S; // dịch miệng→đáy theo +normal
  const pts = path.map((p) =>
    new Vector3(p[0] * S - px, p[1] * S - py, p[2] * S - pz).addScaledVector(n, -lift),
  );
  const pos: number[] = [];
  const seg = (a: Vector3, b: Vector3) => pos.push(a.x, a.y, a.z, b.x, b.y, b.z);
  const toFloor = (v: Vector3) => new Vector3().addVectors(v, n.clone().multiplyScalar(depthScene));
  const dir = new Vector3();
  const wdir = new Vector3();
  const segs: { aL: Vector3; aR: Vector3; bL: Vector3; bR: Vector3 }[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    dir.subVectors(pts[i + 1], pts[i]);
    if (dir.lengthSq() < 1e-12) continue;
    wdir.crossVectors(n, dir);
    if (wdir.lengthSq() < 1e-12) continue;
    wdir.normalize().multiplyScalar(half);
    const aL = new Vector3().addVectors(pts[i], wdir);
    const aR = new Vector3().subVectors(pts[i], wdir);
    const bL = new Vector3().addVectors(pts[i + 1], wdir);
    const bR = new Vector3().subVectors(pts[i + 1], wdir);
    // cạnh dài MIỆNG + ĐÁY cho đoạn này
    seg(aL, bL);
    seg(aR, bR);
    seg(toFloor(aL), toFloor(bL));
    seg(toFloor(aR), toFloor(bR));
    segs.push({ aL, aR, bL, bR });
  }
  if (!segs.length) return null;
  const first = segs[0];
  const last = segs[segs.length - 1];
  for (const [p1, p2] of [
    [first.aL, first.aR], // nắp miệng đầu
    [last.bL, last.bR], // nắp miệng cuối
    [toFloor(first.aL), toFloor(first.aR)], // nắp đáy đầu
    [toFloor(last.bL), toFloor(last.bR)], // nắp đáy cuối
    [first.aL, toFloor(first.aL)], // cạnh đứng đầu-trái
    [first.aR, toFloor(first.aR)], // cạnh đứng đầu-phải
    [last.bL, toFloor(last.bL)], // cạnh đứng cuối-trái
    [last.bR, toFloor(last.bR)], // cạnh đứng cuối-phải
  ] as [Vector3, Vector3][]) {
    seg(p1, p2);
  }
  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(new Float32Array(pos), 3));
  return geo;
}

/** 04d: material viền miệng rãnh — màu/độ mờ do applyAppearance đồng bộ theo nét panel (mode hiện tại). */
export function makeGrooveRimMaterial(): LineBasicMaterial {
  return new LineBasicMaterial({ color: 0x14213d, transparent: true, opacity: 0.9 });
}

// ---------- rãnh LÕM THẬT / CSG recess (04d) ----------

/** 04d: nới cutter khỏi MIỆNG rãnh (mm) — chỉ phía mặt mở, để subtract sạch (KHÔNG nới phía đáy, kẻo thủng mặt sau). */
const GROOVE_MOUTH_EPS_MM = 0.5;
/** 04d: chồng lấn giữa 2 box kề dọc path (mm) — hàn kín khúc cua, tránh hở. */
const GROOVE_SEG_OVERLAP_MM = 0.4;

/**
 * 04d: dựng geometry CUTTER (local scene-space, đã `*S − center` như `buildGrooveGeo`) cho 1 rãnh —
 * chuỗi box định hướng quét dọc `path`. Mỗi đoạn: trục x=đường chạy, y=`widthDir=cross(normal,pathDir)`,
 * z=`normal` (ăn vào vật liệu). Box theo normal trải từ −ε (ngoài miệng) tới +`depth` (đáy) → khoét hở ở
 * mặt mở, không thủng mặt sau. Trả null nếu không dựng được đoạn hợp lệ nào.
 */
function buildGrooveCutterGeo(
  path: [number, number, number][],
  width: number,
  depth: number,
  normal: [number, number, number],
  center: [number, number, number],
): BufferGeometry | null {
  if (!path || path.length < 2) return null;
  const n = new Vector3(normal[0], normal[1], normal[2]);
  if (n.lengthSq() < 1e-9) return null;
  n.normalize();
  const [px, py, pz] = center;
  const eps = GROOVE_MOUTH_EPS_MM * S;
  const ovl = GROOVE_SEG_OVERLAP_MM * S;
  const w = width * S;
  const spanN = depth * S + eps; // tổng bề dày cutter theo normal
  const halfShift = (depth * S - eps) / 2; // tâm cutter lệch khỏi mặt theo +normal
  const pts = path.map(
    (p) => new Vector3(p[0] * S - px, p[1] * S - py, p[2] * S - pz),
  );
  const t = new Vector3();
  const wdir = new Vector3();
  const nAxis = new Vector3();
  const mid = new Vector3();
  const boxes: BufferGeometry[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    t.subVectors(pts[i + 1], pts[i]);
    const len = t.length();
    if (len < 1e-9) continue; // đoạn trùng điểm
    t.multiplyScalar(1 / len);
    // trực giao hoá t theo n (path nằm trên mặt ⇒ t⊥n, nhưng số liệu có thể lệch nhỏ)
    t.addScaledVector(n, -t.dot(n));
    if (t.lengthSq() < 1e-9) continue; // path song song normal (bất thường)
    t.normalize();
    wdir.crossVectors(n, t).normalize();
    nAxis.copy(n);
    mid.addVectors(pts[i], pts[i + 1]).multiplyScalar(0.5).addScaledVector(n, halfShift);
    const box = new BoxGeometry(len + 2 * ovl, w, spanN);
    const m = new Matrix4().makeBasis(t, wdir, nAxis).setPosition(mid);
    box.applyMatrix4(m);
    boxes.push(box);
  }
  if (!boxes.length) return null;
  const merged = boxes.length === 1 ? boxes[0] : mergeGeometries(boxes, false);
  if (boxes.length > 1) boxes.forEach((b) => b.dispose());
  return merged;
}

/**
 * 04d: khoét LÕM THẬT các rãnh của 1 part vào `baseGeo` (CSG subtract) — geometry-agnostic: box phẳng,
 * extrusion (cong), hay mesh-inline đều được, miễn là solid kín. `baseGeo` đã ở local scene-space
 * (căn tâm). Dựng union cutter từ mọi groove rồi `Evaluator.evaluate(SUBTRACTION)`. Trả geometry MỚI đã
 * khoét (`computeVertexNormals` cho tường khe) — hoặc null nếu không có cutter hợp lệ / CSG lỗi (caller
 * fallback decal). KHÔNG đụng `baseGeo` (clone bên trong).
 *
 * `evaluator.attributes` khớp theo base: luôn position+normal; thêm uv CHỈ khi base có (mesh-inline
 * thiếu uv → bỏ uv, tránh three-bvh-csg throw do mismatch; panel có texture → giữ uv để không vỡ vân).
 */
export function carveGrooves(
  baseGeo: BufferGeometry,
  grooves: { path: [number, number, number][]; width: number; depth: number; normal: [number, number, number] }[],
  center: [number, number, number],
): BufferGeometry | null {
  const cutters: BufferGeometry[] = [];
  for (const gr of grooves) {
    const c = buildGrooveCutterGeo(gr.path, gr.width, gr.depth, gr.normal, center);
    if (c) cutters.push(c);
  }
  if (!cutters.length) return null;
  try {
    const cutterGeo = cutters.length === 1 ? cutters[0] : mergeGeometries(cutters, false);
    const base = baseGeo.clone();
    if (!base.getAttribute('normal')) base.computeVertexNormals();
    const attrs = base.getAttribute('uv') ? ['position', 'normal', 'uv'] : ['position', 'normal'];
    const evaluator = new Evaluator();
    evaluator.useGroups = false; // 1 material duy nhất cho part
    evaluator.attributes = attrs;
    const partBrush = new Brush(base);
    partBrush.updateMatrixWorld();
    const cutterBrush = new Brush(cutterGeo);
    cutterBrush.updateMatrixWorld();
    const result = evaluator.evaluate(partBrush, cutterBrush, SUBTRACTION);
    const carved = result.geometry;
    carved.computeVertexNormals(); // tường khe có pháp tuyến đúng → GTAO/đèn ăn
    base.dispose();
    cutters.forEach((c) => c.dispose());
    if (cutterGeo !== cutters[0]) cutterGeo.dispose?.();
    return carved;
  } catch (e) {
    console.warn('[MeshBuilder] CSG khoét rãnh lỗi → fallback decal:', e);
    cutters.forEach((c) => c.dispose());
    return null;
  }
}

/** Tâm part trong tọa độ scene (đã scale). */
export function partCenter(part: WvmPart): [number, number, number] {
  const g = part.geometry;
  if (isBox(g) || isCylinder(g)) return [g.cx * S, g.cy * S, g.cz * S];
  if (isExtrusion(g)) {
    const c = extrusionWorldCenterMM(g);
    return [c.x * S, c.y * S, c.z * S];
  }
  if (isMeshInline(g)) {
    const c = meshWorldCenterMM(g);
    return [c.x * S, c.y * S, c.z * S];
  }
  if (isMeshRef(g)) {
    // gốc instance (local origin → world) = cột dịch chuyển của transform. Đủ dùng cho explode/label.
    return [g.transform[12] * S, g.transform[13] * S, g.transform[14] * S];
  }
  return [0, 0, 0];
}
